import React, { useEffect, useState } from "react";
import { api, fmtEuro, EXPENSE_CATEGORIES } from "@/lib/api";
import { ChartTooltip } from "@/components/ChartTooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from "recharts";
import { Wallet, TrendingDown, TrendingUp, CalendarClock, Repeat, Loader2, AlertTriangle, Pencil, RotateCcw, Check, X } from "lucide-react";
import { toast } from "sonner";

const catLabel = (k) => EXPENSE_CATEGORIES.find((c) => c.key === k)?.label || k;

function formatShortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

export default function Tresorerie() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [manualValue, setManualValue] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/treasury/forecast").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const startEdit = () => {
    setManualValue(data.manual_revenue != null ? String(data.manual_revenue) : String(data.revenu_projete_auto));
    setEditing(true);
  };

  const saveOverride = async () => {
    const v = Number(manualValue);
    if (Number.isNaN(v)) {
      toast.error("Indique un montant valide");
      return;
    }
    setSaving(true);
    try {
      await api.put("/treasury/override", { month: data.month, manual_revenue: v });
      toast.success("Projection mise à jour");
      setEditing(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const resetToAuto = async () => {
    setSaving(true);
    try {
      await api.put("/treasury/override", { month: data.month, manual_revenue: null });
      toast.success("Retour à l'estimation automatique");
      setEditing(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
    );
  }

  const chartData = data.timeline.map((t) => ({ ...t, dateLabel: formatShortDate(t.date) }));
  const soldeBas = data.solde_projete_pessimiste < 0;
  const soldeTendu = !soldeBas && data.solde_projete_pessimiste < data.total_a_venir * 0.3;

  return (
    <div className="space-y-6">
      <div>
        <div className="overline mb-2">Anticiper les creux</div>
        <h1 className="font-manrope text-3xl md:text-4xl font-extrabold">Trésorerie prévisionnelle</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Mois en cours — {data.days_elapsed} j écoulés, {data.days_left} j restants
        </p>
      </div>

      {soldeBas && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 flex items-center gap-3 text-sm text-destructive" data-testid="alert-solde-negatif">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <span>
            Sans nouvelle course, ton solde passerait en <strong>négatif</strong> avant la fin du mois
            (projection pessimiste : {fmtEuro(data.solde_projete_pessimiste)}).
          </span>
        </div>
      )}
      {soldeTendu && (
        <div className="rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 flex items-center gap-3 text-sm text-primary" data-testid="alert-solde-tendu">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <span>Marge serrée en cas de mois calme — surveille les prochaines échéances.</span>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={Wallet} label="Encaissé ce mois" value={fmtEuro(data.ca_month_encaisse)} testid="kpi-encaisse" />
        <Kpi icon={TrendingDown} label="Déjà prélevé" value={fmtEuro(data.deja_preleve)} tone="destructive" testid="kpi-preleve" />
        <Kpi icon={Wallet} label="Solde actuel" value={fmtEuro(data.solde_actuel)} tone="primary" testid="kpi-solde-actuel" />
        <Kpi icon={CalendarClock} label="Charges à venir" value={fmtEuro(data.total_a_venir)} tone="destructive" testid="kpi-a-venir" />
      </div>

      {/* Projections fin de mois */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card-surface p-5">
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown className="h-4 w-4 text-destructive" />
            <h3 className="font-manrope font-semibold text-sm">Projection pessimiste</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Si tu n'encaisses plus rien d'ici la fin du mois</p>
          <div className={`font-manrope font-extrabold text-2xl num ${data.solde_projete_pessimiste < 0 ? "text-destructive" : "text-foreground"}`}>
            {fmtEuro(data.solde_projete_pessimiste)}
          </div>
        </div>
        <div className="card-surface p-5">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[hsl(var(--success))]" />
              <h3 className="font-manrope font-semibold text-sm">Projection réaliste</h3>
            </div>
            {!editing && (
              <div className="flex items-center gap-1">
                {data.revenu_projete_source === "manuel" && (
                  <button onClick={resetToAuto} className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-muted transition-colors" title="Revenir à l'estimation automatique" data-testid="btn-reset-projection">
                    <RotateCcw className="h-3.5 w-3.5" />
                  </button>
                )}
                <button onClick={startEdit} className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-muted transition-colors" title="Fixer moi-même" data-testid="btn-edit-projection">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>

          {editing ? (
            <div className="space-y-2 mt-2">
              <p className="text-xs text-muted-foreground">
                Au rythme actuel ({fmtEuro(data.ca_moyen_jour)}/j · {fmtEuro(data.ca_moyen_jour * 7)}/sem) sur les {data.days_left} j restants — estimation auto : {fmtEuro(data.revenu_projete_auto)}
              </p>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="10"
                  inputMode="decimal"
                  value={manualValue}
                  onChange={(e) => setManualValue(e.target.value)}
                  className="max-w-[160px]"
                  data-testid="input-manual-projection"
                />
                <Button size="sm" onClick={saveOverride} disabled={saving} data-testid="btn-save-projection">
                  <Check className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : (
            <>
              {data.revenu_projete_source === "manuel" ? (
                <>
                  <p className="text-xs text-primary mb-1">
                    Ton rythme visé : {fmtEuro(data.days_left > 0 ? data.revenu_projete_supplementaire / data.days_left : 0)}/j
                    {" "}({fmtEuro(data.days_left > 0 ? (data.revenu_projete_supplementaire / data.days_left) * 7 : 0)}/sem) sur les {data.days_left} j restants
                  </p>
                  <p className="text-xs text-muted-foreground mb-3">
                    (rythme actuel réel : {fmtEuro(data.ca_moyen_jour)}/j · {fmtEuro(data.ca_moyen_jour * 7)}/sem — estimation auto : {fmtEuro(data.revenu_projete_auto)})
                  </p>
                </>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mb-3">
                    Au rythme actuel ({fmtEuro(data.ca_moyen_jour)}/j · {fmtEuro(data.ca_moyen_jour * 7)}/sem) sur les {data.days_left} j restants
                  </p>
                </>
              )}
              <div className="font-manrope font-extrabold text-2xl num text-[hsl(var(--success))]">
                {fmtEuro(data.solde_projete_realiste)}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="card-surface p-4">
        <h3 className="font-manrope font-semibold mb-4">Évolution du solde (scénario pessimiste)</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="dateLabel" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
              <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
              <ReferenceLine y={0} stroke="hsl(var(--destructive))" strokeDasharray="4 4" />
              <Tooltip
                content={
                  <ChartTooltip
                    valueFormatter={(v) => fmtEuro(v)}
                  />
                }
              />
              <Line type="stepAfter" dataKey="solde" name="Solde" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Détail des charges à venir */}
      <div className="card-surface p-5">
        <div className="flex items-center gap-2 mb-4">
          <CalendarClock className="h-4 w-4 text-primary" />
          <h3 className="font-manrope font-semibold">Charges à venir ce mois-ci</h3>
        </div>
        {data.a_venir.length === 0 ? (
          <div className="text-sm text-muted-foreground">Aucune charge en attente — tout est déjà prélevé pour ce mois.</div>
        ) : (
          <div className="space-y-2" data-testid="a-venir-list">
            {data.a_venir.map((item, i) => (
              <div key={i} className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2.5" data-testid={`a-venir-item-${i}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {item.fixe && <Repeat className="h-4 w-4 text-primary shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{item.label}</div>
                    <div className="text-xs text-muted-foreground">{catLabel(item.category)} · {formatShortDate(item.date)}</div>
                  </div>
                </div>
                <div className="text-sm font-semibold num text-destructive shrink-0">-{fmtEuro(item.amount)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone = "muted", testid }) {
  const tones = {
    primary: "text-primary",
    destructive: "text-destructive",
    muted: "text-foreground",
  };
  return (
    <div className="card-surface p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="overline">{label}</span>
      </div>
      <div className={`font-manrope font-extrabold text-lg num ${tones[tone]}`}>{value}</div>
    </div>
  );
}
